import '@framework/browser/polyfill';
import NextApp, { AppProps, AppInitialProps, AppContext, AppType } from 'next/app';
import { Store } from 'redux';
import DefaultSeo from '@framework/seo';
import ErrorBoundary from '@framework/error-handling/ErrorBoundary';
import ApplicationFallback from '@framework/error-handling/ErrorBoundary/ApplicationFallback';
import Layout from '@/finsight/ui/common/components/Layout';
import Bootstrap, { configureApp } from '@framework/bootstrap';
import Head from 'next/head';
import cn from 'classnames';
import Container from '@framework/di/Container';
import { roboto, openSans } from '@framework/font';
import Favicon from '@finsight/favicon';
import GlobalProps from '@framework/router/Next/GlobalProps';
import { IGlobalProps } from '@framework/router/Next/withGlobalProps';
import TimeZoneContextProvider from '@framework/timezone/TimeZoneContext';
import { rootModules } from '@/finsight/application/DI/rootModules';
import Header from '@/finsight/ui/common/components/header/FinsightHeader/HeaderContainer';
import { footerCollection as footerConfig } from '@/finsight/ui/common/components/Footer/finsightFooterCollection';
import PageLoadInstrumentation from '@framework/otel/Next/PageLoadInstrumentation';
import PageNavigationInstrumentation from '@framework/otel/Next/PageNavigationInstrumentation';
import { InitAlerts } from '@creditflowresearch/uilib/Alert';

import '@/finsight/ui/styles/globalStyles.scss';

const AppComponent = ({ Component, pageProps, container }: AppProps<IGlobalProps> & {
  container: Container,
  store?: Store,
}) => {
  return (
    <>
      <Favicon />
      <Head>
        <title />
        <meta charSet="utf-8" />
        <meta name="referrer" content="no-referrer-when-downgrade" />
        <meta httpEquiv="Cache-Control" content="no-store, no-cache, must-revalidate, proxy-revalidate, max-age=0" />
        <meta httpEquiv="Pragma" content="no-store" />
        <meta httpEquiv="Expires" content="0" />
        <meta content="initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no" name="viewport" />
        <meta name="HandheldFriendly" content="true" />
      </Head>
      <ErrorBoundary fallback={ ApplicationFallback }>
        <Bootstrap
          container={ container }
          session={ pageProps?.session }
          cookies={ pageProps?.cookies }
          renderAlerts={ <InitAlerts /> }
        >
          <PageLoadInstrumentation>
            <GlobalProps { ...pageProps }>
              <TimeZoneContextProvider>
                <DefaultSeo />
                <Layout
                  HeaderComponent={ Header }
                  footerConfig={ footerConfig }
                  className={ cn(roboto.variable, openSans.variable) }
                >
                  <PageNavigationInstrumentation>
                    <Component { ...pageProps } />
                  </PageNavigationInstrumentation>
                </Layout>
              </TimeZoneContextProvider>
            </GlobalProps>
          </PageLoadInstrumentation>
        </Bootstrap>
      </ErrorBoundary>
    </>
  );
};

const App = configureApp(AppComponent, rootModules) as AppType;

/*
 * For some reason, when generating pages like 404,
 * next.js does not pass the request and response fields in _documents.getInitialProps.
 * Which breaks the process of adding nonce to these pages. This is a workaround for this problem.
 * For more information, see https://github.com/vercel/next.js/discussions/64023.
 */
App.getInitialProps = async (appContext: AppContext): Promise<AppInitialProps> => {
  const appProps = await NextApp.getInitialProps(appContext);
  return appProps;
};

export default App;
